import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CALL_FILTER_STATUSES, CALL_STATUS_ENTITY, RECENT_CALLS_PAGE_MAX, Size, callLengthText, callStatusPill, callsCsv, callsQueryPath, formatDateTime, formatPhone,
  ourNumber, recordHref, t, theirNumber,
  type CallsFilter, type ColumnDef, type DateFormat, type PageSize, type PagedRecord, type ReadableNumber, type RecentCallRow, type RecentCallsAnswer,
} from '@bms2/shared';
import { Button, DatePicker, Select } from '../../components/controls';
import { ListViewer } from '../../components/composites/ListViewer';
import { Icon } from '../../components/Icon';
import { useDateFormat, useViewerZone } from '../../shell/ViewerZone';
import { get, useApi } from '../records/api';
import { saveTextFile } from '../import/saveTextFile';
import { CallWindow } from './CallWindow';
import styles from './CallList.module.css';

/** A call as the list draws it: the read's row with each cell's words beside it. */
export interface CallListRow extends RecentCallRow {
  directionText: string;
  theirText: string;
  ourText: string;
  recordRoute: string | null;
  timeText: string;
  lengthText: string;
}

/** The direction, their number, our number, the record, the time, the length and the status (decision 11860). */
export const CALL_COLUMNS: readonly ColumnDef[] = [
  { key: 'directionText', label: t('phonePages.col.direction'), role: 'secondary', type: 'marks', sortable: false },
  { key: 'theirText', label: t('phonePages.col.theirNumber'), role: 'primary', type: 'text', sortable: false },
  { key: 'recordName', label: t('phonePages.col.record'), role: 'secondary', type: 'link', linkPattern: ':recordRoute', sortable: false },
  { key: 'timeText', label: t('phonePages.col.time'), role: 'secondary', type: 'text', sortable: false },
  { key: 'lengthText', label: t('phonePages.col.length'), role: 'secondary', type: 'text', sortable: false, fit: true },
  { key: 'ourText', label: t('phonePages.col.ourNumber'), role: 'secondary', type: 'text', sortable: false },
  { key: 'status', label: t('phonePages.col.status'), role: 'status', type: 'status', statusEntity: CALL_STATUS_ENTITY, sortable: false },
];

/** The record a call names, as a page the router serves; a call on no record names none. */
function recordRouteOf(row: RecentCallRow): string | null {
  if (!row.recordId || (row.recordType !== 'PersonRecord' && row.recordType !== 'CompanyRecord')) return null;
  return recordHref(row.recordType as PagedRecord, row.recordId);
}

/** 🔒 Every cell's words, the time through the shared zoned helper in the viewer's zone and format (decision 0261). */
export function callListRow(row: RecentCallRow, zone: string, dateFormat: DateFormat): CallListRow {
  return {
    ...row,
    directionText: t(row.direction === 'Inbound' ? 'phonePages.direction.inbound' : 'phonePages.direction.outbound'),
    theirText: formatPhone(theirNumber(row)) || t('phonePages.unknownCaller'),
    ourText: formatPhone(ourNumber(row)),
    recordRoute: recordRouteOf(row),
    timeText: formatDateTime(row.at, zone, dateFormat),
    lengthText: callLengthText(row.durationSeconds),
  };
}

/** Our number as the filter offers it: the number, then General or Rep. */
function numberOption(row: ReadableNumber): { value: string; label: string } {
  return { value: row.id, label: `${formatPhone(row.number)} · ${t(row.kind === 'General' ? 'phonePages.filter.general' : 'phonePages.filter.rep')}` };
}

/** No bound on status, number or day. */
const NO_FILTER: CallsFilter = { status: '', numberId: '', from: '', to: '' };

export interface CallListProps {
  list: 'calls' | 'voicemail';
  apiBaseUrl: string;
  columns: readonly ColumnDef[];
  /** The list's own words: its title for the band and its empty line. */
  recordType: string;
  emptyAction: string;
  /** Words a page adds to each row for a column of its own, the Voicemail list's transcript line; the export reads them too. */
  rowWords?: (row: RecentCallRow) => Record<string, string>;
  /** A cell the page draws itself, the Voicemail list's Play and Done. */
  cellMarker?: (row: CallListRow, key: string, reload: () => void) => ReactNode;
}

/**
 * 🔒 Calls and Voicemail (decision 11860): one List Viewer over `GET calls` or `GET calls/voicemail`, filtered by status
 * and by our number (Calls also by day, decision 11900), paged on the server, newest first. A row whose call wrote a note opens the record's page, where
 * the note heads its Notes; any other row opens the call's own window. Export writes the filtered rows' visible
 * columns, read page by page from the same route.
 */
export function CallList({ list, apiBaseUrl, columns, recordType, emptyAction, rowWords, cellMarker }: CallListProps) {
  const zone = useViewerZone();
  const dateFormat = useDateFormat();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<CallsFilter>(NO_FILTER);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<PageSize>(50);
  const [opened, setOpened] = useState<CallListRow | null>(null);
  const [exportRefusal, setExportRefusal] = useState<string | null>(null);
  const fetched = useApi<RecentCallsAnswer>(callsQueryPath(list, filter, page, pageSize), apiBaseUrl);
  const answer = fetched.state.kind === 'ready' ? fetched.state.data : null;
  const drawn = (row: RecentCallRow): CallListRow => ({ ...callListRow(row, zone, dateFormat), ...rowWords?.(row) });
  const rows = (answer?.rows ?? []).map(drawn);
  const numbers = answer?.numbers ?? [];
  const filtered = filter.status !== '' || filter.numberId !== '' || filter.from !== '' || filter.to !== '';

  const choose = (next: Partial<CallsFilter>) => {
    setFilter((was) => ({ ...was, ...next }));
    setPage(1);
  };

  /** 🔒 Export reads every page of the filter at the largest page the route serves, then writes the file (decision 11860). */
  const exportCalls = async () => {
    setExportRefusal(null);
    const all: RecentCallRow[] = [];
    for (let at = 1; ; at += 1) {
      const read = await get<RecentCallsAnswer>(callsQueryPath(list, filter, at, RECENT_CALLS_PAGE_MAX), apiBaseUrl);
      if (!read.ok) return setExportRefusal(t('phonePages.calls.exportFailed'));
      all.push(...read.data.rows);
      if (read.data.rows.length === 0 || all.length >= read.data.total) break;
    }
    const shown = columns.map((column) => ({ key: column.key === 'status' ? 'statusText' : column.key, label: column.label }));
    const cells = all.map((row) => {
      return { ...drawn(row), statusText: callStatusPill(row.status)?.label ?? row.status } as unknown as Record<string, unknown>;
    });
    saveTextFile(`${list}.csv`, 'text/csv', callsCsv(shown, cells));
  };

  const open = (row: CallListRow) => {
    if (row.noteId && row.recordRoute) navigate(row.recordRoute);
    else setOpened(row);
  };

  const filters = (
    <span className={styles.filters} data-call-filters={list}>
      {list === 'calls' ? (
        <Select
          size={Size.Small}
          label={t('phonePages.filter.status')}
          value={filter.status}
          onChange={(value) => choose({ status: value as CallsFilter['status'] })}
          placeholder={t('phonePages.filter.any')}
          options={CALL_FILTER_STATUSES.map((status) => ({ value: status, label: callStatusPill(status)?.label ?? status }))}
          dataAttribute={{ 'data-call-filter': 'status' }}
        />
      ) : null}
      <Select
        size={Size.Medium}
        label={t('phonePages.filter.number')}
        value={filter.numberId}
        onChange={(value) => choose({ numberId: value })}
        placeholder={t('phonePages.filter.any')}
        options={numbers.map(numberOption)}
        dataAttribute={{ 'data-call-filter': 'number' }}
      />
      {list === 'calls' ? (
        <>
          <DatePicker size={Size.Small} label={t('phonePages.filter.from')} value={filter.from} onChange={(value) => choose({ from: value })} {...(filter.to !== '' ? { max: filter.to } : {})} />
          <DatePicker size={Size.Small} label={t('phonePages.filter.to')} value={filter.to} onChange={(value) => choose({ to: value })} {...(filter.from !== '' ? { min: filter.from } : {})} />
        </>
      ) : null}
      <span className={styles.export}>
        <Button size={Size.Small} text={[t('phonePages.calls.export')]} actions={[() => void exportCalls()]} />
      </span>
    </span>
  );

  return (
    <div className={styles.list} data-call-list={list} data-call-rows={String(rows.length)}>
      {exportRefusal ? <p className={styles.refusal} role="alert">{exportRefusal}</p> : null}
      <ListViewer
        recordType={recordType}
        views={[{ id: 'all', name: recordType }]}
        currentViewId="all"
        onViewChange={() => undefined}
        columns={columns}
        rows={rows}
        idOf={(row) => row.id}
        read={(row, key) => (row as unknown as Record<string, unknown>)[key]}
        total={answer?.total ?? 0}
        sort={null}
        onSortChange={() => undefined}
        sortLocked={t('phonePages.calls.sortLocked')}
        pageSize={pageSize}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
        serverPage={{ page, onPageChange: setPage, scope: `${list}|${filter.status}|${filter.numberId}|${filter.from}|${filter.to}` }}
        status={{ resolve: (_entity, value) => callStatusPill(value), entityTypeFor: () => CALL_STATUS_ENTITY }}
        state={fetched.state}
        onRetry={fetched.reload}
        emptyAction={emptyAction}
        {...(filtered ? { filterNoMatch: { onClear: () => choose(NO_FILTER) } } : {})}
        selectable={false}
        filters={filters}
        onNavigate={open}
        onRoute={(route) => navigate(route)}
        cellMarker={(row, key) => {
          if (key === 'directionText') return <Icon meaning={row.direction === 'Inbound' ? 'callInbound' : 'callOutbound'} label={row.directionText} />;
          return cellMarker?.(row, key, fetched.reload) ?? null;
        }}
      />
      {opened ? <CallWindow key={opened.id} row={opened} zone={zone} dateFormat={dateFormat} onClose={() => setOpened(null)} /> : null}
    </div>
  );
}
